import { Controller } from "@hotwired/stimulus";
import I18n from "../../i18n/translations";
import { dsConfirm } from "../../utils/ds_confirm";

export default class extends Controller {
  static targets = [
    "modal",
    "form",
    "modalTitle",
    "feesList",
    "classesList",
    "installmentsList",
    "discountsList",
    "activitiesList",
    "statusBadge",
    "activeBadge",
  ];

  static values = {
    filterPath: String,
    currentFeeId: Number,
    baseUrl: String,
    feeTypesIndexTitle: String,
  };

  connect() {
    this.initializeTooltips();
    this.currentFilters = { feeTypeId: null };
    // Les onglets du détail (show) sont désormais gérés par le contrôleur DS
    // `ds-tabs` (aria + clavier). Ce contrôleur ne conserve que le filtrage par
    // type de frais (index) et les actions lock/unlock/AJAX.
  }

  initializeTooltips() {
    // Initialise les tooltips si nécessaire
  }

  // Gestion des filtres
  filterByAcademicYear(event) {
    const academicYearId = event.target.value;
    this.currentFilters.academicYearId = academicYearId || null;
    this.applyFilters();
  }

  filterByFeeType(event) {
    // État actif via aria-selected (onglets DS pill `.ds-tab`).
    event.currentTarget
      .closest("[role='tablist']")
      .querySelectorAll("[role='tab']")
      .forEach((tab) => tab.setAttribute("aria-selected", "false"));
    event.currentTarget.setAttribute("aria-selected", "true");

    // Récupérer l'ID du type de frais
    const feeTypeId = event.currentTarget.dataset.feeTypeId;

    if (!feeTypeId) {
      // Si c'est l'onglet "Tous les types"
      this.currentFilters.feeTypeId = null;
    } else {
      this.currentFilters.feeTypeId = feeTypeId;
    }

    this.applyFilters();
  }

  applyFilters() {
    let url = this.filterPathValue;
    // Les filtres du formulaire (statut, verrou, classe, année scolaire) sont
    // dans l'URL de la page : l'onglet de type s'y ajoute au lieu de les
    // effacer.
    const params = new URLSearchParams(window.location.search);
    params.delete("fee_type_id");
    params.delete("page");

    if (this.currentFilters.feeTypeId) {
      params.append("fee_type_id", this.currentFilters.feeTypeId);
    }

    if (params.toString()) {
      url = `${url}?${params.toString()}`;
    }

    fetch(url, {
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        if (this.hasFeesListTarget) {
          this.feesListTarget.innerHTML = html;
        }
      })
      .catch((error) => console.error("Error:", error));
  }

  // Gestion des modales
  openModal(event) {
    event.preventDefault();
    const url = event.currentTarget.getAttribute("href");
    const title = event.currentTarget.getAttribute("data-title");

    this.modalTitleTarget.textContent = title;

    fetch(url, {
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        this.formTarget.innerHTML = html;
        this.modalTarget.classList.remove("hidden");
      })
      .catch((error) => {
        console.error("Error:", error);
        this.showNotification(
          I18n.t("schools.fees.flash.server_error"),
          "error",
        );
      });
  }

  closeModal() {
    this.modalTarget.classList.add("hidden");
    this.formTarget.innerHTML = "";
  }

  // Reactivating a locked fee bills the students enrolled while it was
  // deactivated (owner's decision of 03/10, night): the link carries the DS
  // confirm announcing how many and the total; otherwise it carries none.
  async activate(event) {
    event.preventDefault();
    // The button of a PATCH form (never a link: QA4 FS4-03).
    const url = event.currentTarget.form.action;
    const { confirm, confirmTitle, confirmLabel } = event.params;
    if (
      confirm &&
      !(await dsConfirm(confirm, { title: confirmTitle, confirmLabel }))
    )
      return;

    this.updateStatus(url, "activate");
  }

  // A locked fee has billed its students: deactivating it is confirmed
  // first (owner's decision of 03/10). The link carries the DS confirm's
  // texts as action params; an unlocked fee carries none.
  async deactivate(event) {
    event.preventDefault();
    // The button of a PATCH form (never a link: QA4 FS4-03).
    const url = event.currentTarget.form.action;
    const { confirm, confirmTitle, confirmLabel } = event.params;
    if (
      confirm &&
      !(await dsConfirm(confirm, { title: confirmTitle, confirmLabel }))
    )
      return;

    this.updateStatus(url, "deactivate");
  }

  updateStatus(url, _action) {
    fetch(url, {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          this.afterStatusChange(data.message);
        } else {
          this.showNotification(
            data.message || I18n.t("schools.fees.flash.server_error"),
            "error",
          );
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        this.showNotification(
          I18n.t("schools.fees.flash.server_error"),
          "error",
        );
      });
  }

  // The list of fees is filtered again. On the fee's own page there is no
  // list: the page kept « Actif » and « Désactiver » after a deactivation,
  // and the user clicked again (QA3 FS-08, SF-09). It is drawn again, and
  // the confirmation shown once it is.
  afterStatusChange(message) {
    if (!this.element.querySelector('[data-fees-manager-target="feeHeader"]')) {
      this.applyFilters();
      this.showNotification(message, "success");
      return;
    }

    document.addEventListener(
      "turbo:load",
      () => this.showNotification(message, "success"),
      { once: true },
    );
    window.Turbo.visit(window.location.href, { action: "replace" });
  }

  showNotification(message, type) {
    const notificationDiv = document.createElement("div");
    notificationDiv.className = `fixed top-4 right-4 p-4 rounded-sm shadow-lg z-50 ${
      type === "success" ? "bg-cta" : "bg-red-500"
    } text-white`;
    notificationDiv.textContent = message;

    document.body.appendChild(notificationDiv);

    setTimeout(() => {
      notificationDiv.remove();
    }, 3000);
  }

  // Soumission du formulaire
  submitForm(event) {
    event.preventDefault();
    const form = event.currentTarget;

    fetch(form.action, {
      method: form.method,
      body: new FormData(form),
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          this.closeModal();
          this.showNotification(data.message, "success");

          // Rafraîchir la liste ou rediriger
          if (data.redirect) {
            window.location.href = data.redirect;
          }
        } else {
          // Afficher les erreurs
          const errorFields = document.querySelectorAll("[class^='error-']");
          errorFields.forEach((field) => {
            field.textContent = "";
          });

          Object.keys(data.errors || {}).forEach((key) => {
            const errorField = document.querySelector(`.error-${key}`);
            if (errorField) {
              errorField.textContent = data.errors[key].join(", ");
            }
          });

          this.showNotification(
            I18n.t("schools.fees.flash.validation_error"),
            "error",
          );
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        this.showNotification(
          I18n.t("schools.fees.flash.server_error"),
          "error",
        );
      });
  }

  async delete(event) {
    event.preventDefault();
    const confirmMessage = event.currentTarget.dataset.confirm;
    const href = event.currentTarget.href;

    if (await dsConfirm(confirmMessage, { tone: "danger" })) {
      const form = document.createElement("form");
      form.method = "POST";
      form.action = href;

      const methodInput = document.createElement("input");
      methodInput.type = "hidden";
      methodInput.name = "_method";
      methodInput.value = "delete";

      const tokenInput = document.createElement("input");
      tokenInput.type = "hidden";
      tokenInput.name = "authenticity_token";
      tokenInput.value = document.querySelector(
        'meta[name="csrf-token"]',
      ).content;

      form.appendChild(methodInput);
      form.appendChild(tokenInput);
      document.body.appendChild(form);
      form.submit();
    }
  }
}
